import { Cluster } from "@/components/primitives/cluster";
import { TagPill } from "@/components/ui/tag-pill";
import { cn } from "@/lib/utils";

export interface PostCardProps {
	title: string;
	href: string;
	created?: string;
	modified?: string;
	excerpt?: string;
	tags?: string[];
	thumbnail?: string;
	className?: string;
}

// Homepage / archive post card: thumbnail + dates + title + excerpt + tags,
// all token-driven. The whole card is the link (tags render as plain spans
// to avoid nested anchors).
export function PostCard({
	title,
	href,
	created,
	modified,
	excerpt,
	tags,
	thumbnail,
	className,
}: PostCardProps) {
	return (
		<a
			href={href}
			className={cn(
				// no-underline: preflight is omitted, so the native <a> would
				// otherwise underline (and link-color) all child text.
				"card-surface group block rounded-lg border border-border p-5 no-underline transition-colors hover:border-accent",
				className,
			)}
		>
			{thumbnail ? (
				<img
					src={thumbnail}
					alt=""
					loading="lazy"
					// bg-white: transparent logos (e.g. Ubuntu/Python) sit on white like
					// the baked-white thumbnails instead of the dark card showing through.
					// Cyan-accent frame in dark (the white box is jarring on a bare border);
					// light keeps the neutral border (looks right there already).
					className="mb-3 h-40 w-full rounded-md border border-accent/40 bg-white object-cover [[data-theme=light]_&]:border-border"
				/>
			) : null}
			{created ? (
				<div className="font-mono text-subtle text-xs leading-snug">
					<div>Created: {created}</div>
					{/* Always render the Modified line so the second row of text is
					    reserved and titles line up across cards. When there's no
					    modified date it's hidden but still occupies the space. */}
					<div className={modified ? undefined : "invisible"} aria-hidden={!modified}>
						Modified: {modified ?? created}
					</div>
				</div>
			) : null}
			<h3 className="mt-1 font-mono font-semibold text-default text-lg group-hover:text-accent">
				{title}
			</h3>
			{excerpt ? <p className="mt-2 text-muted text-sm">{excerpt}</p> : null}
			{tags && tags.length > 0 ? (
				<Cluster gap={2} className="mt-3">
					{tags.map((t) => (
						<TagPill key={t}>{t}</TagPill>
					))}
				</Cluster>
			) : null}
		</a>
	);
}
